/**
 * Services — what the UI calls (through hooks). They validate input, then delegate to the DataProvider,
 * and compose read models (daily report, management analytics) from provider data.
 * Pages never call a provider directly and never hold business records of their own.
 */
import type {
  Backup,
  DeadlineChangeInput,
  MemberInput,
  ParsedDay,
  ProjectInput,
  ProjectPatch,
  ProjectUpdateFilter,
  ProjectUpdateInput,
  Settings,
  WorkLogFilter,
  WorkLogInput,
} from '@/types/models';
import { DataError, type DataProvider } from './DataProvider';
import { deadline } from '@/domain/projects';
import { buildReport, reportData, type DailyReport } from '@/domain/report';
import { computeManagementAnalytics } from '@/domain/analytics/management';
import type { ManagementAnalytics, ManagementFilter, ProjectAnalysis } from '@/domain/analytics/types';
import { normalizeBackup } from '@/domain/backup';
import {
  hasErrors,
  validateDeadlineChange,
  validateMember,
  validateProject,
  validateProjectUpdate,
  validateWorkLog,
  type FieldErrors,
} from '@/domain/validation';

/** Thrown when input fails validation; `details` holds the field errors. */
const invalid = (e: FieldErrors) => new DataError(Object.values(e)[0], 422, { fields: e });
const check = (e: FieldErrors) => {
  if (hasErrors(e)) throw invalid(e);
};
const trim = (s: unknown) => String(s ?? '').trim();

export function createServices(provider: DataProvider) {
  const p = provider;

  const projects = {
    list: () => p.listProjects(),
    get: (id: string) => p.getProject(id),
    create: async (input: ProjectInput) => {
      check(validateProject(input));
      return p.createProject({ ...input, name: trim(input.name), service: trim(input.service), lead: trim(input.lead), completed: trim(input.completed), pending: trim(input.pending) });
    },
    update: async (id: string, patch: ProjectPatch) => {
      if ('name' in patch) check(validateProject(patch));
      const clean: ProjectPatch = { ...patch };
      (['name', 'service', 'lead', 'completed', 'pending'] as const).forEach((k) => {
        if (clean[k] !== undefined) clean[k] = trim(clean[k]);
      });
      return p.updateProject(id, clean);
    },
    remove: (id: string) => p.deleteProject(id),
    changeDeadline: async (id: string, input: DeadlineChangeInput) => {
      const cur = await p.getProject(id);
      if (!cur) throw new DataError('Project not found.', 404);
      check(validateDeadlineChange(input, deadline(cur)));
      return p.changeDeadline(id, { to: input.to, reason: trim(input.reason) });
    },
  };

  const members = {
    list: () => p.listMembers(),
    get: (id: string) => p.getMember(id),
    create: async (input: MemberInput) => {
      check(validateMember(input));
      return p.createMember({ name: trim(input.name), role: trim(input.role) });
    },
    update: async (id: string, patch: Partial<MemberInput>) => {
      if ('name' in patch) check(validateMember(patch));
      return p.updateMember(id, { ...patch, ...(patch.name !== undefined && { name: trim(patch.name) }), ...(patch.role !== undefined && { role: trim(patch.role) }) });
    },
    remove: (id: string) => p.deleteMember(id),
  };

  const workLogs = {
    list: (filter?: WorkLogFilter) => p.listWorkLogs(filter),
    create: async (input: WorkLogInput) => {
      check(validateWorkLog(input));
      return p.createWorkLog({ ...input, hours: Number(input.hours), work: trim(input.work) });
    },
    update: async (id: string, input: WorkLogInput) => {
      check(validateWorkLog(input));
      return p.updateWorkLog(id, { ...input, hours: Number(input.hours), work: trim(input.work) });
    },
    remove: (id: string) => p.deleteWorkLog(id),
  };

  const projectUpdates = {
    list: (filter?: ProjectUpdateFilter) => p.listProjectUpdates(filter),
    create: async (input: ProjectUpdateInput) => {
      check(validateProjectUpdate(input));
      return p.createProjectUpdate({ ...input, note: trim(input.note) });
    },
    update: (id: string, patch: Partial<ProjectUpdateInput>) => p.updateProjectUpdate(id, { ...patch, ...(patch.note !== undefined && { note: trim(patch.note) }) }),
    remove: (id: string) => p.deleteProjectUpdate(id),
  };

  const settings = {
    get: () => p.getSettings(),
    update: (patch: Partial<Settings>) => p.updateSettings({ ...patch, ...(patch.reportTitle !== undefined && { reportTitle: trim(patch.reportTitle) }) }),
  };

  /** Loads the five collections (used where a page needs everything, as the original app did). */
  const loadAll = async (): Promise<Backup> => {
    const [ps, ms, ls, us, st] = await Promise.all([p.listProjects(), p.listMembers(), p.listWorkLogs(), p.listProjectUpdates(), p.getSettings()]);
    return { projects: ps, members: ms, logs: ls, updates: us, settings: st };
  };

  const reports = {
    /** The day's report as structured data (formatted view). Only that day's logs and updates are fetched. */
    getDailyReport: async (date: string): Promise<DailyReport> => {
      const [ps, ms, ls, us, st] = await Promise.all([p.listProjects(), p.listMembers(), p.listWorkLogs({ from: date, to: date }), p.listProjectUpdates({ from: date, to: date }), p.getSettings()]);
      return reportData({ projects: ps, members: ms, logs: ls, updates: us, settings: st }, date);
    },
    /** The copyable plain text — byte-identical to the original app's "Copy report". */
    generateDailyReport: async (date: string): Promise<string> => buildReport(await reports.getDailyReport(date)),
  };

  const analytics = {
    /** Server-side when the provider supports it, otherwise computed here from the provider's lists (same rules). */
    getManagementAnalytics: async (filter: ManagementFilter): Promise<ManagementAnalytics> =>
      p.getManagementAnalytics ? p.getManagementAnalytics(filter) : computeManagementAnalytics(await loadAll(), filter),
    getProjectAnalytics: async (projectId: string, filter: ManagementFilter): Promise<ProjectAnalysis> => {
      const a = await analytics.getManagementAnalytics(filter);
      return { row: a.rows.find((r) => r.id === projectId) || null, metrics: a.metrics.filter((m) => m.projectId === projectId), extensions: a.extensions.filter((e) => e.projectId === projectId) };
    },
    getTeamAnalytics: async (filter: ManagementFilter) => {
      const a = await analytics.getManagementAnalytics(filter);
      return { team: a.team, byMember: a.byMember, activeMemberIds: a.activeMemberIds, lastWorked: a.lastWorked };
    },
    getActivityAnalytics: async (filter: ManagementFilter) => {
      const a = await analytics.getManagementAnalytics(filter);
      return { range: a.range, hours: a.hours, previous: a.previous, logs: a.scopeLogs, updates: a.updates };
    },
  };

  const backup = {
    export: () => p.exportBackup(),
    import: (data: Backup) => p.importBackup(normalizeBackup(data)),
    importDailyUpdate: (days: ParsedDay[]) => p.importDailyUpdate(days),
    resetDemo: () => (p.resetDemoData ? p.resetDemoData() : Promise.reject(new DataError('Not available.', 405))),
    deleteAll: () => (p.deleteAllData ? p.deleteAllData() : Promise.reject(new DataError('Not available.', 405))),
  };

  return { provider, projects, members, workLogs, projectUpdates, settings, reports, analytics, backup, loadAll };
}

export type Services = ReturnType<typeof createServices>;
